import React from 'react';
import { Neo, Views, NeoGrid } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import { TextConstants } from '../../../Common/TextConstants';
import ClientAccountMappingVM from './ClientAccountMappingVM';
import RunInterceptionComponent from '../../../Common/Components/Interception/RunInterceptionComponent';
import MissingAccountLookup from '../Models/MissingAccountLookup';
import ExcludedAccountsView from '../../ExclusionAccounts/Views/ExcludedAccountsView';
import Loading from '../../../App/Components/Loading/Loading';
import ClientLookup from '../Models/ClientLookup';
import ManageClientAccountComponent from '../Components/ManageClientAccountComponent';
import { ClientAccountMapping, ExcludedAccounts, MissingAccounts } from '../../../AccessControl/Models/Commands/Security/Roles';
import { InterceptionProcessEnum } from '../../../Common/Enums/InterceptionProcessEnum';

@observer
export default class ClientAccountMappingView extends Views.ViewBase<ClientAccountMappingVM> {
    constructor(props: unknown) {
        super("Client Account Mapping", ClientAccountMappingVM, props);
    }

    public render() {
        return (
            <>
                {(this.viewModel.taskRunner.isBusy) && <Loading />}
                {this.viewModel.showManageClientAccountMappingComponent === true &&
                    <ManageClientAccountComponent
                        viewModel={this.viewModel.manageClientAccountMappingVM}
                        onSave={() => this.viewModel.saveClientAccount()}
                        onBack={() => this.viewModel.backToClientAccountView()} />
                }
                {this.viewModel.showManageClientAccountMappingComponent === false &&
                    <Neo.TabContainer>
                        <Neo.Tab header={TextConstants.Titles.MissingAccountMappings} >
                            {this.viewModel.authorisationService.hasRole(MissingAccounts.Access) &&
                                <Neo.Card title={TextConstants.Titles.MissingAccountMappings} className="m-1">
                                    <Neo.Pager pageManager={this.viewModel.missingAccountPageManager} pageControlProps={{ pageSizeLabel: "Show: " }}>
                                        <NeoGrid.Grid<MissingAccountLookup> className={"table-striped"}>
                                            {(item, meta) => (
                                                <NeoGrid.Row key={item.missingAccountId}>
                                                    <NeoGrid.Column display={meta.serviceProvider} />
                                                    <NeoGrid.Column label={TextConstants.Labels.Broker} display={meta.stockbroker} />
                                                    <NeoGrid.Column display={meta.accountReference} />
                                                    {this.viewModel.authorisationService.hasRole(MissingAccounts.MapAccount) &&
                                                        <NeoGrid.ButtonColumn>
                                                            <Neo.Button
                                                                onClick={() => this.viewModel.mapMissingAccount(meta.missingAccountId.value, meta.staticServiceProviderFileGroupingId.value, meta.accountReference.value!)}
                                                                variant="primary" size="sm"
                                                            >{TextConstants.Buttons.MapAccount}</Neo.Button>
                                                        </NeoGrid.ButtonColumn>
                                                    }
                                                    {this.viewModel.authorisationService.hasRole(MissingAccounts.ExcludeAccount) &&
                                                        <NeoGrid.ButtonColumn>
                                                            <Neo.Button
                                                                variant="secondary" size="sm"
                                                                onClick={() => this.viewModel.showExcludeMissingAccountModal(item!)}
                                                            >{TextConstants.Buttons.ExcludeAccount}</Neo.Button>
                                                        </NeoGrid.ButtonColumn>
                                                    }
                                                </NeoGrid.Row>
                                            )}
                                        </NeoGrid.Grid>
                                    </Neo.Pager>
                                    <Neo.Modal
                                        title={TextConstants.Titles.ConfirmAccountExclusion}
                                        bind={this.viewModel.meta.showExcludeMissingAccountModalInd}
                                        closeButton={{ text: TextConstants.Buttons.No, variant: "primary", isOutline: true }}
                                        acceptButton={{ text: TextConstants.Buttons.Yes, variant: "secondary", onClick: () => this.viewModel.excludeMissingAccount() }}>
                                        <p>{TextConstants.GeneralText.ExcludeAccount1}{this.viewModel.missingAccountNumberToExclude}{TextConstants.GeneralText.ExcludeAccount2}</p>
                                        <ul>
                                            <li>{TextConstants.GeneralText.ExcludeAccount3}</li>
                                            <li>{TextConstants.GeneralText.ExcludeAccount4}</li>
                                        </ul>
                                    </Neo.Modal>
                                </Neo.Card>
                            }

                            {!this.viewModel.showExcludedAccountsComponent &&
                                <RunInterceptionComponent
                                    viewModel={this.viewModel.runInterceptionVM}
                                    interceptionStoredProcedure={InterceptionProcessEnum.Clients} />}
                        </Neo.Tab>
                        <Neo.Tab header={TextConstants.Titles.ClientAccounts} >
                            {this.viewModel.authorisationService.hasRole(ClientAccountMapping.Access) &&
                                <Neo.Card title={TextConstants.Titles.ClientAccounts} className="m-1">
                                    {this.viewModel.authorisationService.hasRole(ClientAccountMapping.AddNew) &&
                                        <div className="row pb-3">
                                            <div className="col text-right">
                                                <Neo.Button
                                                    onClick={() => this.viewModel.mapMissingAccount()}
                                                    variant="primary" className="ml-3"
                                                >{TextConstants.Buttons.AddClientAccount}</Neo.Button>
                                            </div>
                                        </div>
                                    }
                                    <Neo.Form
                                        model={this.viewModel.clientAccountSearchCriteria}
                                        onSubmit={() => this.viewModel.clientAccountSearchCriteria.meta.searchCriteria.value === "" ? this.viewModel.clientAccountPageManager.refreshData() : this.viewModel.clearSearch()}>
                                        <Neo.FormGroup className="form-search"
                                            bind={this.viewModel.clientAccountSearchCriteria.meta.searchCriteria}
                                            placeholder={TextConstants.PlaceHolder.SearchClientAccounts}
                                            suppressLabel={true}
                                            append={<Neo.Button isSubmit>
                                                <i className={`${this.viewModel.clientAccountSearchCriteria.meta.searchCriteria.value === "" ? "fa fa-search" : "fa fa-times-circle"}`} />
                                            </Neo.Button>}
                                        />
                                    </Neo.Form>
                                    <Neo.Pager pageManager={this.viewModel.clientAccountPageManager} pageControlProps={{ pageSizeLabel: "Show: " }}>
                                        <NeoGrid.Grid<ClientLookup> className={"table-striped"}>
                                            {(item, meta) => (
                                                <NeoGrid.RowGroup expandProperty={meta.isExpanded}>
                                                    <NeoGrid.Row key={item.clientId}>
                                                        <NeoGrid.Column display={meta.clientNumber} />
                                                        <NeoGrid.Column display={meta.currencyCode} />
                                                        <NeoGrid.Column display={meta.clientName} />
                                                        <NeoGrid.Column display={meta.clientEntityTypeName} />
                                                    </NeoGrid.Row>
                                                    {item.clientAccounts.length > 0 &&
                                                        <NeoGrid.ChildRow>
                                                            <NeoGrid.Grid
                                                                items={item.clientAccounts}>
                                                                {(child, childMeta) => (
                                                                    <NeoGrid.Row>
                                                                        <NeoGrid.Column display={childMeta.serviceProviderAccountNumber} />
                                                                        <NeoGrid.Column display={childMeta.brokerName} />
                                                                        <NeoGrid.Column display={childMeta.brokerDescription} />
                                                                        <NeoGrid.Column display={childMeta.activeInactive} />
                                                                        <NeoGrid.ButtonColumn label={TextConstants.Labels.Edit} className="text-center">
                                                                            <Neo.Button
                                                                                variant="primary" isOutline className="btn-small"
                                                                                onClick={() => this.viewModel.editClient(child, item.meta.clientId.value)}>
                                                                                <i className="fa fa-edit"></i>
                                                                            </Neo.Button>
                                                                        </NeoGrid.ButtonColumn>
                                                                    </NeoGrid.Row>
                                                                )}
                                                            </NeoGrid.Grid>
                                                        </NeoGrid.ChildRow>
                                                    }
                                                </NeoGrid.RowGroup>
                                            )}
                                        </NeoGrid.Grid>
                                    </Neo.Pager>
                                </Neo.Card>
                            }
                        </Neo.Tab>
                        <Neo.Tab header={TextConstants.Titles.ExcludedAccounts} >
                            {this.viewModel.authorisationService.hasRole(ExcludedAccounts.Access) &&
                                <ExcludedAccountsView
                                    viewModel={this.viewModel.excludedAccountsVM}
                                    clientAccountVM={this.viewModel} />
                            }
                        </Neo.Tab>
                    </Neo.TabContainer>
                }
            </>
        )
    }
}